import React, { JSX, useState } from 'react'
import { NumberInput } from 'components/NumberInput'
import classNames from 'classnames'
import { Dropdown } from 'components/Dropdown'

const FieldLabel = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <span className='mb-1.5 block text-xs font-medium uppercase tracking-wider text-dm-text-accent'>
    {children}
  </span>
)

const checkerboard: React.CSSProperties = {
  backgroundImage:
    'linear-gradient(45deg, rgba(255,255,255,0.15) 25%, transparent 25%), ' +
    'linear-gradient(-45deg, rgba(255,255,255,0.15) 25%, transparent 25%), ' +
    'linear-gradient(45deg, transparent 75%, rgba(255,255,255,0.15) 75%), ' +
    'linear-gradient(-45deg, transparent 75%, rgba(255,255,255,0.15) 75%)',
  backgroundSize: '8px 8px',
  backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0'
}

interface ParsedColor { hex: string, alpha: number }

const HEX6 = /^#([0-9a-fA-F]{6})$/
const HEX8 = /^#([0-9a-fA-F]{6})([0-9a-fA-F]{2})$/

const parseColor = (value: string): ParsedColor => {
  if (value === 'transparent') return { hex: '#000000', alpha: 0 }
  const m8 = HEX8.exec(value)
  if (m8 != null) {
    return {
      hex: ('#' + m8[1]).toUpperCase(),
      alpha: Math.round((Number.parseInt(m8[2], 16) / 255) * 100)
    }
  }
  const m6 = HEX6.exec(value)
  if (m6 != null) return { hex: ('#' + m6[1]).toUpperCase(), alpha: 100 }
  return { hex: '#000000', alpha: 100 }
}

const composeColor = (hex: string, alpha: number): string => {
  if (alpha <= 0) return 'transparent'
  if (alpha >= 100) return hex
  const a = Math.round((alpha / 100) * 255).toString(16).padStart(2, '0').toUpperCase()
  return `${hex}${a}`
}

interface ColorInputProps {
  label?: string
  value: string
  onChange: (next: string) => void
  inline?: boolean
  solidOnly?: boolean
}

export const ColorInput = ({
  label,
  value,
  onChange,
  inline = false,
  solidOnly = false
}: ColorInputProps): JSX.Element => {
  const { hex, alpha } = parseColor(value)
  const effAlpha = solidOnly ? 100 : alpha

  const setHex = (nextHex: string): void => {
    onChange(composeColor(nextHex, effAlpha))
  }
  const setAlpha = (next: number): void => {
    onChange(composeColor(hex, Math.max(0, Math.min(100, next))))
  }

  const isFullyTransparent = !solidOnly && alpha === 0

  const content = (
    <div className='space-y-2'>
      <div className='flex items-center gap-2'>
        <div
          className={classNames(
            'relative h-9 w-12 shrink-0 overflow-hidden rounded-md border border-white/10'
          )}
          style={isFullyTransparent ? checkerboard : undefined}
        >
          <input
            type='color'
            value={hex}
            onChange={(e) => setHex(e.target.value.toUpperCase())}
            className='h-full w-full cursor-pointer p-0'
            style={isFullyTransparent ? { opacity: 0 } : undefined}
          />
        </div>
        <input
          type='text'
          value={solidOnly ? hex : value}
          onChange={(e) => {
            if (solidOnly) {
              const parsed = parseColor(e.target.value)
              onChange(composeColor(parsed.hex, 100))
            } else {
              onChange(e.target.value)
            }
          }}
          className='min-w-0 flex-1 rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 font-mono text-xs text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
        />
      </div>
      {!solidOnly && (
        <div className='flex items-center gap-2'>
          <span className='shrink-0 text-[10px] font-medium uppercase tracking-wider text-dm-text-accent'>
            Opacity
          </span>
          <input
            type='range'
            min='0'
            max='100'
            step='1'
            value={alpha}
            onChange={(e) => setAlpha(Number.parseInt(e.target.value, 10))}
            className='h-1.5 flex-1 cursor-pointer appearance-none rounded-full bg-dm-bg-accent accent-brand'
          />
          <NumberInput
            value={alpha}
            min={0}
            max={100}
            onCommit={setAlpha}
            aria-label='Opacity percent'
            className='w-14 shrink-0 rounded-md border border-white/10 bg-dm-bg-accent/40 px-2 py-1 text-right font-mono text-[11px] text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
          />
          <span className='shrink-0 text-[11px] text-dm-text-accent'>%</span>
        </div>
      )}
    </div>
  )

  if (inline) return content
  return (
    <label className='block'>
      {label !== undefined && <FieldLabel>{label}</FieldLabel>}
      {content}
    </label>
  )
}

type BgMode = 'solid' | 'gradient' | 'custom'

interface ParsedGradient {
  angle: number
  start: string
  end: string
}

const GRADIENT_RE = /^linear-gradient\(\s*(\d+)deg\s*,\s*(#[0-9a-fA-F]{6}(?:[0-9a-fA-F]{2})?|transparent|rgba?\([^)]+\))(?:\s+\d+%)?\s*,\s*(#[0-9a-fA-F]{6}(?:[0-9a-fA-F]{2})?|transparent|rgba?\([^)]+\))(?:\s+\d+%)?\s*\)$/i

const parseLinearGradient = (value: string): ParsedGradient | null => {
  const m = GRADIENT_RE.exec(value)
  if (m == null) return null
  const normalize = (color: string): string =>
    color.startsWith('#') ? color.toUpperCase() : color
  return {
    angle: Number.parseInt(m[1], 10),
    start: normalize(m[2]),
    end: normalize(m[3])
  }
}

const composeLinearGradient = (g: ParsedGradient): string =>
  `linear-gradient(${g.angle}deg, ${g.start}, ${g.end})`

const detectMode = (value: string): BgMode => {
  if (value === 'transparent') return 'solid'
  if (HEX6.test(value) || HEX8.test(value)) return 'solid'
  if (parseLinearGradient(value) !== null) return 'gradient'
  return 'custom'
}

const ANGLE_PRESETS: Array<{ value: string, label: string }> = [
  { value: '180', label: '↓ Top → bottom' },
  { value: '0', label: '↑ Bottom → top' },
  { value: '90', label: '→ Left → right' },
  { value: '270', label: '← Right → left' },
  { value: '135', label: '↘ Top-left → bottom-right' },
  { value: '45', label: '↗ Bottom-left → top-right' },
  { value: '225', label: '↙ Top-right → bottom-left' },
  { value: '315', label: '↖ Bottom-right → top-left' }
]

const Preview = ({ value }: { value: string }): JSX.Element => (
  <div
    className='h-10 w-full overflow-hidden rounded-md border border-white/10 bg-clip-padding'
    style={{ background: value }}
    title='Preview'
  />
)

interface BackgroundInputProps {
  label: string
  value: string
  onChange: (next: string) => void
}

export const BackgroundInput = ({ label, value, onChange }: BackgroundInputProps): JSX.Element => {
  const [mode, setMode] = useState<BgMode>(() => detectMode(value))
  const seed = parseLinearGradient(value) ?? { angle: 180, start: '#000000', end: '#FFFFFF' }
  const [gradient, setGradient] = useState<ParsedGradient>(seed)

  const updateGradient = (patch: Partial<ParsedGradient>): void => {
    const next = { ...gradient, ...patch }
    setGradient(next)
    onChange(composeLinearGradient(next))
  }

  const handleModeChange = (next: BgMode): void => {
    setMode(next)
    if (next === 'solid') {
      if (value === 'transparent' || HEX6.test(value) || HEX8.test(value)) return
      const g = parseLinearGradient(value)
      onChange(g?.start ?? '#FFFFFF')
    } else if (next === 'gradient') {
      if (HEX6.test(value) || HEX8.test(value) || value === 'transparent') {
        const seeded = { ...gradient, start: value === 'transparent' ? '#000000' : value }
        setGradient(seeded)
        onChange(composeLinearGradient(seeded))
      } else if (parseLinearGradient(value) === null) {
        onChange(composeLinearGradient(gradient))
      }
    }
  }

  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div className='flex flex-col gap-3'>
        <div className='inline-flex w-fit rounded-md border border-white/10 bg-dm-bg-accent/40 p-0.5 text-[11px]'>
          {(['solid', 'gradient', 'custom'] as BgMode[]).map((m) => (
            <button
              key={m}
              type='button'
              onClick={() => handleModeChange(m)}
              className={classNames(
                'rounded px-2.5 py-1 capitalize transition-colors',
                mode === m
                  ? 'bg-brand/15 text-brand'
                  : 'text-dm-text-secondary hover:text-dm-text-primary'
              )}
            >
              {m}
            </button>
          ))}
        </div>

        {mode === 'solid' && (
          <ColorInput value={value} onChange={onChange} inline />
        )}

        {mode === 'gradient' && (
          <div className='space-y-3 rounded-md border border-white/5 bg-dm-bg-accent/20 p-3'>
            <Preview value={value} />
            <div>
              <FieldLabel>Start</FieldLabel>
              <ColorInput
                value={gradient.start}
                onChange={(v) => updateGradient({ start: v })}
                inline
              />
            </div>
            <div>
              <FieldLabel>End</FieldLabel>
              <ColorInput
                value={gradient.end}
                onChange={(v) => updateGradient({ end: v })}
                inline
              />
            </div>
            <div>
              <FieldLabel>Direction</FieldLabel>
              <Dropdown
                value={String(gradient.angle)}
                options={ANGLE_PRESETS}
                onChange={(v) => updateGradient({ angle: Number.parseInt(v, 10) })}
              />
            </div>
          </div>
        )}

        {mode === 'custom' && (
          <div className='space-y-2'>
            <input
              type='text'
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder='linear-gradient(45deg, #foo, #bar) any CSS background value'
              className='w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 font-mono text-xs text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
            />
            <Preview value={value} />
            <p className='text-[11px] text-dm-text-accent'>
              Accepts anything the CSS <code>background</code> property does radial gradients,
              multi-stop, image URLs, patterns.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
